import {
  put,
  all,
  take,
  takeEvery,
  fork,
  select,
  call
} from "redux-saga/effects";

import { apiCall } from "App/Data/sagas";
import { registerError } from "redux/modules/errors/actions";

import { actions as TabsActions } from "ui-kit/Tabs";

import { actions, getters } from "./model";

import {
  TAB_IDS,
  TAB_INSTANCE_IDS,
  FIELD_IDS,
  FORM_INSTANCE_ID
} from "./constants";

import * as R from "ramda";

import { actions as FormActions } from "ui-kit/Form/model";

import { showSnackbar } from "redux/modules/snackbar/actions";
import { eventDetails as getPortalEventDetails } from "redux/modules/portal/selectors";

import { actions as ContactsActions } from "./View/ProfileInfo/Contacts/model";
import { getEditedField } from "./selectors";
import { decorateValueToSave } from "Portal/NewPortal/utils";

const refreshData = function*() {
  const eventDetails = yield select(getPortalEventDetails);

  const {
    payload: {
      bio,
      values,
      fields,
      visible_field_ids: visibleFieldIds,
      locked_field_ids: lockedFieldIds,
      enable_additional_contacts: enableAdditionalContacts
    }
  } = yield call(apiCall, {
    method: "get",
    url: `/portal/event/${eventDetails.uuid}/profile`
  });

  yield put(ContactsActions.initFetchContactData());

  yield put(
    actions.setInitialData({
      bio,
      fields,
      visibleFieldIds,
      lockedFieldIds,
      enableAdditionalContacts,
      data: { ...values }
    })
  );
};

const getContactMembers = function*() {
  try {
    yield put(actions.setLoadingContacts(true));
    const eventDetails = yield select(getPortalEventDetails);
    const { payload } = yield call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/profile/contact-members`
    });
    yield put(actions.setContactMembers(payload));
    yield put(actions.setLoadingContacts(false));
  } catch (error) {
    yield put(actions.setLoadingContacts(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while getting contacts"
        }
      ])
    );
  }
};

const deleteContactMembers = function*({ payload: contactMembers }) {
  try {
    const eventDetails = yield select(getPortalEventDetails);
    yield call(apiCall, {
      method: "delete",
      url: `/portal/event/${eventDetails.uuid}/profile/contact-members`,
      data: {
        contactMembers
      }
    });

    // show  confirmation
    yield put(showSnackbar({ message: "Contacts Updated" }));

    yield call(getContactMembers);
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while deleting additional contacts"
        }
      ])
    );
  }
};

const getAccountContacts = function*() {
  try {
    yield put(actions.setLoadingContacts(true));
    const eventDetails = yield select(getPortalEventDetails);
    const { payload } = yield call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/profile/contact-members`
    });
    yield put(actions.setContactMembers(payload));
    yield put(actions.setLoadingContacts(false));
  } catch (error) {
    yield put(actions.setLoadingContacts(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while getting contacts"
        }
      ])
    );
  }
};

const deleteAccountContacts = function*({ payload: accountContacts }) {
  try {
    const eventDetails = yield select(getPortalEventDetails);
    yield call(apiCall, {
      method: "delete",
      url: `/portal/event/${eventDetails.uuid}/profile/account-contacts`,
      data: {
        accountContacts
      }
    });

    // show  confirmation
    yield put(showSnackbar({ message: "Contacts Updated" }));

    yield call(getAccountContacts);
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while deleting account contacts"
        }
      ])
    );
  }
};

const init = function*() {
  // load details
  try {
    yield put(actions.setLoading(true));

    yield call(refreshData);

    yield put(
      TabsActions.setSelectedTab(TAB_IDS.PROFILE_INFO, {
        meta: {
          instanceId: TAB_INSTANCE_IDS
        }
      })
    );

    yield put(actions.setLoading(false));
  } catch (error) {
    yield put(actions.setLoading(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while getting profile information"
        }
      ])
    );
  }
};

const cancelSave = function*() {
  yield put(actions.setUpdatedFieldValues({}));
  yield call(init);
};

const save = function*(
  { payload: { viaUnsavedChangesModal } } = {
    payload: { viaUnsavedChangesModal: false }
  }
) {
  try {
    yield put(actions.setSaving(true));

    const eventDetails = yield select(getPortalEventDetails);
    const fields = yield select(getters.fields);
    const updatedFieldValues = yield select(getters.updatedFieldValues);
    const lockedFieldIds = yield select(getters.lockedFieldIds);
    const transitionToTabIdAfterSave = yield select(
      getters.transitionToTabIdAfterSave
    );

    // decorate values to send to backend
    const formattedValues = Object.keys(updatedFieldValues).reduce(
      (a, fieldId) => {
        const field = fields.find(f => f.id === fieldId);
        const isLockedField = lockedFieldIds.includes(fieldId);

        if (!isLockedField && field) {
          const value = updatedFieldValues[fieldId];
          a.push(decorateValueToSave({ fieldId, field, value }));
        }
        return a;
      },
      []
    );

    // save data
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${eventDetails.uuid}/profile/values`,
      data: {
        values: formattedValues
      }
    });

    // show save confirmation
    yield put(showSnackbar({ message: "Details Saved" }));

    // show new tab that the user originally was intending to go to
    if (viaUnsavedChangesModal && transitionToTabIdAfterSave) {
      yield put(
        TabsActions.setSelectedTab(transitionToTabIdAfterSave, {
          meta: {
            instanceId: TAB_INSTANCE_IDS
          }
        })
      );
    }

    yield all([
      put(actions.setSaving(false)),
      put(actions.setTransitionToTabIdAfterSave(null)),
      put(actions.setUpdatedFieldValues({}))
    ]);

    yield call(refreshData);

    return true;
  } catch (error) {
    yield put(actions.setSaving(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while saving details"
        }
      ])
    );
    return false;
  }
};

const fieldUpdate = function*({ id, fieldId }) {
  const newValue = yield select(getEditedField, {
    instanceId: id,
    fieldId
  });

  const processedValue = R.propOr(newValue, fieldId, {
    [FIELD_IDS.LOGO_IMAGE_URL]: R.pathOr(null, [0, "url"], newValue),
    [FIELD_IDS.BACKGROUND_IMAGE_URL]: R.pathOr(null, [0, "url"], newValue)
  });

  yield put(
    actions.setUpdatedFieldValue({
      fieldId,
      value: processedValue
    })
  );
};

const watchUpdateFields = function*() {
  for (;;) {
    const {
      meta: { instanceId, fieldId }
    } = yield take(FormActions.setFieldValue.type);
    if (instanceId === FORM_INSTANCE_ID) {
      yield call(fieldUpdate, {
        id: instanceId,
        fieldId
      });
    }
  }
};

const onCancelUnsavedChanges = function*() {
  yield put(actions.setHasUnSavedChangesModal(false));
};

const onConfirmUnsavedChanges = function*() {
  yield put(actions.setHasUnSavedChangesModal(false));
  yield put(actions.save({ viaUnsavedChangesModal: true }));
};

const watchOnCancelUnsavedChanges = function*() {
  yield takeEvery(actions.onCancelUnsavedChanges.type, onCancelUnsavedChanges);
};
const watchOnConfirmUnsavedChanges = function*() {
  yield takeEvery(
    actions.onConfirmUnsavedChanges.type,
    onConfirmUnsavedChanges
  );
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};
const watchSave = function*() {
  yield takeEvery(actions.save.type, save);
};
const watchCancel = function*() {
  yield takeEvery(actions.cancel.type, cancelSave);
};
const watchGetContactMembers = function*() {
  yield takeEvery(actions.getContactMembers.type, getContactMembers);
};
const watchGetAccountContacts = function*() {
  yield takeEvery(actions.getAccountContacts.type, getAccountContacts);
};
const watchDeleteContactMembers = function*() {
  yield takeEvery(actions.deleteContactMembers.type, deleteContactMembers);
};
const watchDeleteAccountContacts = function*() {
  yield takeEvery(actions.deleteAccountContacts.type, deleteAccountContacts);
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchSave),
    fork(watchCancel),
    fork(watchUpdateFields),
    fork(watchOnCancelUnsavedChanges),
    fork(watchOnConfirmUnsavedChanges),
    fork(watchGetContactMembers),
    fork(watchGetAccountContacts),
    fork(watchDeleteContactMembers),
    fork(watchDeleteAccountContacts)
  ]);
};

export default rootSaga;
